import React, {useState, useEffect, useRef, useCallback} from 'react';
import logoIsotipo from './images/logo_isotipo.svg';
import {Link, Navigate, useLocation} from "react-router-dom";
import {LocalStorage} from "./utils/LocalStorage";
import {AccessService} from "./utils/AccessService";
import {APIClient} from "./utils/APIClient";
import {API_URL} from "./settings/local_settings";
import SuperAdminSwitcher from "./components/super_admin/SuperAdminSwitcher";
import {useTranslation} from 'react-i18next';
import {useSidebar} from "./context/SidebarContext";
import {
    List,
    ChartBar,
    UsersThree,
    MagnifyingGlass,
    Robot,
    FileText,
    Lock,
    CaretUpDown,
    CheckCircle,
    Circle,
    Link as LinkIcon,
    DownloadSimple,
    Gear,
    Users,
    Bell,
    CreditCard,
    Plugs,
    ChartPie,
    Buildings,
    Question,
    CaretDown,
} from "@phosphor-icons/react";
import {MiniOnboardingBar} from "./components/help/MiniOnboardingBar";
import {useHelpDrawer} from "./components/help/HelpDrawerContext";

// Onboarding tasks definition
const ONBOARDING_TASKS = [
    {key: 'conexiones', label: 'sidebar.onboarding_connections'},
    {key: 'cliente', label: 'sidebar.onboarding_client'},
    {key: 'presupuestos', label: 'sidebar.onboarding_budgets'},
    {key: 'alertas', label: 'sidebar.onboarding_alerts'},
    {key: 'reportes', label: 'sidebar.onboarding_reports'},
    {key: 'explorador', label: 'sidebar.onboarding_explorer'},
    {key: 'insights', label: 'sidebar.onboarding_insights'},
    {key: 'agente', label: 'sidebar.onboarding_agent'},
];

function OnboardingWidget({t}) {
    const [completedTasks, setCompletedTasks] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem('dashbo_onboarding') || '[]');
        } catch { return []; }
    });

    const toggleTask = useCallback((key) => {
        setCompletedTasks(prev => {
            const next = prev.includes(key)
                ? prev.filter(k => k !== key)
                : [...prev, key];
            try { localStorage.setItem('dashbo_onboarding', JSON.stringify(next)); } catch {}
            return next;
        });
    }, []);

    const completed = completedTasks.length;
    const total = ONBOARDING_TASKS.length;
    const pct = Math.round((completed / total) * 100);

    // Don't show if all tasks completed
    if (completed >= total) return null;

    return (
        <div className="sidebar-v2-onboarding">
            <div className="sidebar-v2-onboarding-header">
                <span className="sidebar-v2-onboarding-title">{t('sidebar.onboarding_title')}</span>
                <span className="sidebar-v2-onboarding-progress">{completed}/{total}</span>
            </div>
            <div className="sidebar-v2-onboarding-bar">
                <div className="sidebar-v2-onboarding-bar-fill" style={{width: `${pct}%`}}/>
            </div>
            <div className="sidebar-v2-onboarding-list">
                {ONBOARDING_TASKS.map(task => {
                    const isDone = completedTasks.includes(task.key);
                    return (
                        <div
                            key={task.key}
                            className={`sidebar-v2-onboarding-item${isDone ? ' done' : ''}`}
                            onClick={() => toggleTask(task.key)}
                        >
                            <span className="sidebar-v2-onboarding-icon">
                                {isDone
                                    ? <CheckCircle size={16} weight="fill"/>
                                    : <Circle size={16}/>
                                }
                            </span>
                            <span>{t(task.label)}</span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function LayoutSidebarV2() {
    const {t} = useTranslation();
    const {isCollapsed, toggleSidebar, isMobile, isMobileOpen, closeMobileSidebar} = useSidebar();
    const {openDrawer: openHelpDrawer} = useHelpDrawer();
    const location = useLocation();

    // Cerrar sidebar mobile al navegar
    useEffect(() => {
        if (isMobile && isMobileOpen) {
            closeMobileSidebar();
        }
    }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

    const [loggedOut, setLoggedOut] = useState(false);
    const [modules, setModules] = useState(null);
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);
    const [isToolsOpen, setIsToolsOpen] = useState(false);
    const [isAgencyMenuOpen, setIsAgencyMenuOpen] = useState(false);
    const [agencyData, setAgencyData] = useState(() => {
        // Show cached agency data instantly while API loads
        try {
            const cached = localStorage.getItem('dashbo_agency_data');
            return cached ? JSON.parse(cached) : null;
        } catch { return null; }
    });
    const [showAgencyDropdown, setShowAgencyDropdown] = useState(false);
    const agencyTriggerRef = useRef(null);
    const agencyDropdownRef = useRef(null);

    const isClientUser = LocalStorage.isClientUser();

    useEffect(() => {
        const userId = LocalStorage.fetchUserId();
        if (userId) {
            AccessService.GetUserModules(userId,
                (data) => setModules(data.modules),
                () => {
                    LocalStorage.removeUserData();
                    setLoggedOut(true);
                }
            );
        }
    }, []);

    useEffect(() => {
        new APIClient(API_URL + 'get-logged-user-data/').call('GET').then((data) => {
            if (data && data.status === 'OK') {
                setIsSuperAdmin(data.is_super_admin === true);
                if (data.agency_id) {
                    LocalStorage.updateAgencyId(String(data.agency_id));
                }
            }
        });
    }, []);

    useEffect(() => {
        new APIClient(API_URL + 'get-agency-data/').call('GET').then((response) => {
            if (response.status === 'OK') {
                setAgencyData(response.general_data);
                try {
                    localStorage.setItem('dashbo_agency_data', JSON.stringify(response.general_data));
                } catch { /* ignore */ }
            }
        });
    }, []);

    // Close dropdown on outside click
    useEffect(() => {
        function handleClick(e) {
            if (showAgencyDropdown &&
                agencyDropdownRef.current && !agencyDropdownRef.current.contains(e.target) &&
                agencyTriggerRef.current && !agencyTriggerRef.current.contains(e.target)) {
                setShowAgencyDropdown(false);
            }
        }
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [showAgencyDropdown]);

    const toggleAgencyDropdown = useCallback(() => {
        setShowAgencyDropdown(prev => !prev);
    }, []);

    if (isClientUser) return null;
    if (loggedOut) return <Navigate to="/login"/>;
    if (modules === null) return null;

    const hasModule = (mod) => modules.includes(mod);
    const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

    const agencyInitials = agencyData?.name
        ? agencyData.name.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase()
        : '?';

    const hasMarketing = hasModule(AccessService.MODULE_MARKETING);
    const isToolsActive = isActive('/dashbo-connect') ||
        isActive('/sheets-sync') ||
        isActive('/agency/alerts-configuration');
    const isAgencyActive = isActive('/agency/conections') ||
        isActive('/agency-team') ||
        isActive('/agency/subscription') ||
        isActive('/agency/profile');
    const showToolsMenu = isToolsOpen || isToolsActive;
    const showAgencyMenu = isAgencyMenuOpen || isAgencyActive;

    // Position agency dropdown above the trigger
    const getAgencyDropdownStyle = () => {
        if (!agencyTriggerRef.current) return {};
        const rect = agencyTriggerRef.current.getBoundingClientRect();
        return {
            left: rect.left,
            bottom: window.innerHeight - rect.top + 4,
        };
    };

    return (
        <>
            <aside className={`sidebar-v2${isCollapsed && !isMobile ? ' collapsed' : ''}${isMobile ? ' mobile' : ''}${isMobileOpen ? ' mobile-open' : ''}`}>
                {/* Header — toggle left, logo centered */}
                <div className="sidebar-v2-header">
                    <button className="sidebar-v2-toggle" onClick={toggleSidebar} title={isCollapsed ? 'Expandir' : 'Colapsar'}>
                        <List size={22} weight="bold"/>
                    </button>
                    <img src={logoIsotipo} className="sidebar-v2-logo-img" alt="Dashbo"/>
                </div>

                {/* Nav — only PRINCIPAL section (5 items) */}
                <nav className="sidebar-v2-nav">
                    <div className="sidebar-v2-section-label">{t('sidebar.section_main')}</div>

                    {hasMarketing && (
                        <Link className={`sidebar-v2-link${isActive('/marketing') ? ' active' : ''}`} to="/marketing">
                            <span className="sidebar-v2-link-icon"><ChartBar size={20}/></span>
                            <span className="sidebar-v2-link-text">{t('sidebar.tablero')}</span>
                            <span className="sidebar-v2-tooltip">{t('sidebar.tablero')}</span>
                        </Link>
                    )}

                    {hasMarketing && (
                        <Link className={`sidebar-v2-link${isActive('/agency/clients') || isActive('/client/') ? ' active' : ''}`} to="/agency/clients">
                            <span className="sidebar-v2-link-icon"><UsersThree size={20}/></span>
                            <span className="sidebar-v2-link-text">{t('sidebar.clientes')}</span>
                            <span className="sidebar-v2-tooltip">{t('sidebar.clientes')}</span>
                        </Link>
                    )}

                    {hasModule(AccessService.MODULE_DATA_EXPLORER) ? (
                        <Link className={`sidebar-v2-link${isActive('/data-explorer') ? ' active' : ''}`} to="/data-explorer">
                            <span className="sidebar-v2-link-icon"><MagnifyingGlass size={20}/></span>
                            <span className="sidebar-v2-link-text">{t('sidebar.explorador')}</span>
                            <span className="sidebar-v2-tooltip">{t('sidebar.explorador')}</span>
                        </Link>
                    ) : (
                        <Link className="sidebar-v2-link -locked" to="/data-explorer">
                            <span className="sidebar-v2-link-icon" style={{position: 'relative'}}>
                                <MagnifyingGlass size={20}/>
                                <Lock size={10} weight="bold" style={{position: 'absolute', bottom: -2, right: -4, background: 'var(--primary)', color: '#fff', borderRadius: '50%', padding: 1}}/>
                            </span>
                            <span className="sidebar-v2-link-text">{t('sidebar.explorador')}</span>
                            <span className="sidebar-v2-tooltip">{t('sidebar.explorador')}</span>
                        </Link>
                    )}

                    {hasModule(AccessService.MODULE_AGENT) && (
                        <Link className={`sidebar-v2-link${isActive('/agent/chat') ? ' active' : ''}`} to="/agent/chat">
                            <span className="sidebar-v2-link-icon"><Robot size={20}/></span>
                            <span className="sidebar-v2-link-text">{t('sidebar.dashbito')}</span>
                            <span className="sidebar-v2-tooltip">{t('sidebar.dashbito')}</span>
                        </Link>
                    )}

                    {hasModule(AccessService.MODULE_REPORTS) && (
                        <Link className={`sidebar-v2-link${isActive('/reports') ? ' active' : ''}`} to="/reports">
                            <span className="sidebar-v2-link-icon"><FileText size={20}/></span>
                            <span className="sidebar-v2-link-text">{t('sidebar.reportes')}</span>
                            <span className="sidebar-v2-tooltip">{t('sidebar.reportes')}</span>
                        </Link>
                    )}

                    <button
                        type="button"
                        className={`sidebar-v2-link sidebar-v2-link-button sidebar-v2-menu-toggle${isToolsActive ? ' active' : ''}${showToolsMenu ? ' open' : ''}`}
                        onClick={() => setIsToolsOpen(prev => !prev)}
                        aria-expanded={showToolsMenu}
                    >
                        <span className="sidebar-v2-link-icon"><Gear size={20}/></span>
                        <span className="sidebar-v2-link-text">{t('sidebar.dropdown_tools')}</span>
                        <span className="sidebar-v2-menu-caret"><CaretDown size={14} weight="bold"/></span>
                        <span className="sidebar-v2-tooltip">{t('sidebar.dropdown_tools')}</span>
                    </button>

                    {showToolsMenu && (
                        <div className="sidebar-v2-submenu">
                            <Link className={`sidebar-v2-link${isActive('/agency/alerts-configuration') ? ' active' : ''}`} to="/agency/alerts-configuration">
                                <span className="sidebar-v2-link-text">{t('sidebar.dropdown_alerts')}</span>
                                <span className="sidebar-v2-tooltip">{t('sidebar.dropdown_alerts')}</span>
                            </Link>

                            {hasModule(AccessService.MODULE_DASHBO_MCP) && (
                                <Link className={`sidebar-v2-link${isActive('/dashbo-connect') ? ' active' : ''}`} to="/dashbo-connect">
                                    <span className="sidebar-v2-link-text">{t('sidebar.connect')}</span>
                                    <span className="sidebar-v2-tooltip">{t('sidebar.connect')}</span>
                                </Link>
                            )}

                            {hasModule(AccessService.MODULE_SHEETS_SYNC) && (
                                <Link className={`sidebar-v2-link${isActive('/sheets-sync') ? ' active' : ''}`} to="/sheets-sync">
                                    <span className="sidebar-v2-link-text">{t('sidebar.exportacion')}</span>
                                    <span className="sidebar-v2-tooltip">{t('sidebar.exportacion')}</span>
                                </Link>
                            )}

                            {hasModule(AccessService.MODULE_DATA_EXPLORER) && (
                                <a className="sidebar-v2-link" href="https://lookerstudio.google.com/" target="_blank" rel="noopener noreferrer">
                                    <span className="sidebar-v2-link-text">{t('sidebar.looker_studio')}</span>
                                    <span className="sidebar-v2-tooltip">{t('sidebar.looker_studio')}</span>
                                </a>
                            )}
                        </div>
                    )}

                    <button
                        type="button"
                        className={`sidebar-v2-link sidebar-v2-link-button sidebar-v2-menu-toggle${isAgencyActive ? ' active' : ''}${showAgencyMenu ? ' open' : ''}`}
                        onClick={() => setIsAgencyMenuOpen(prev => !prev)}
                        aria-expanded={showAgencyMenu}
                    >
                        <span className="sidebar-v2-link-icon"><Buildings size={20}/></span>
                        <span className="sidebar-v2-link-text">{t('sidebar.dropdown_agency')}</span>
                        <span className="sidebar-v2-menu-caret"><CaretDown size={14} weight="bold"/></span>
                        <span className="sidebar-v2-tooltip">{t('sidebar.dropdown_agency')}</span>
                    </button>

                    {showAgencyMenu && (
                        <div className="sidebar-v2-submenu">
                            <Link className={`sidebar-v2-link${isActive('/agency/conections') ? ' active' : ''}`} to="/agency/conections">
                                <span className="sidebar-v2-link-text">{t('sidebar.dropdown_connections')}</span>
                                <span className="sidebar-v2-tooltip">{t('sidebar.dropdown_connections')}</span>
                            </Link>

                            <Link className={`sidebar-v2-link${isActive('/agency-team') ? ' active' : ''}`} to="/agency-team">
                                <span className="sidebar-v2-link-text">{t('sidebar.dropdown_team')}</span>
                                <span className="sidebar-v2-tooltip">{t('sidebar.dropdown_team')}</span>
                            </Link>

                            <Link className={`sidebar-v2-link${isActive('/agency/subscription') ? ' active' : ''}`} to="/agency/subscription">
                                <span className="sidebar-v2-link-text">{t('sidebar.dropdown_subscription')}</span>
                                <span className="sidebar-v2-tooltip">{t('sidebar.dropdown_subscription')}</span>
                            </Link>

                            <Link className={`sidebar-v2-link${isActive('/agency/profile') ? ' active' : ''}`} to="/agency/profile">
                                <span className="sidebar-v2-link-text">{t('sidebar.dropdown_profile')}</span>
                                <span className="sidebar-v2-tooltip">{t('sidebar.dropdown_profile')}</span>
                            </Link>
                        </div>
                    )}

                    <button
                        type="button"
                        className="sidebar-v2-link sidebar-v2-link-button"
                        onClick={() => openHelpDrawer('basic')}
                    >
                        <span className="sidebar-v2-link-icon"><Question size={20}/></span>
                        <span className="sidebar-v2-link-text">{t('sidebar.ayuda')}</span>
                        <span className="sidebar-v2-tooltip">{t('sidebar.ayuda')}</span>
                    </button>

                    {/* Dashbo Setup mini progress — directly under the Ayuda link */}
                    <MiniOnboardingBar/>

                    {/* Spacer pushes agency switcher to bottom */}
                    <div className="sidebar-v2-spacer"/>
                </nav>

                {/* Agency switcher (bottom) */}
                {isSuperAdmin && <SuperAdminSwitcher/>}
                <div
                    ref={agencyTriggerRef}
                    className="sidebar-v2-agency-trigger"
                    onClick={toggleAgencyDropdown}
                >
                    <div className="sidebar-v2-agency-icon">{agencyInitials}</div>
                    <div className="sidebar-v2-agency-info">
                        <div className="sidebar-v2-agency-name">{agencyData?.name || ''}</div>
                        <div className="sidebar-v2-agency-plan">{agencyData?.plan_name || ''}</div>
                    </div>
                    <span className="sidebar-v2-agency-caret"><CaretUpDown size={16}/></span>
                </div>
            </aside>

            {/* Agency dropdown */}
            <div
                ref={agencyDropdownRef}
                className={`sidebar-v2-agency-dropdown${showAgencyDropdown ? ' show' : ''}`}
                style={showAgencyDropdown ? getAgencyDropdownStyle() : {}}
            >
                <div className="sidebar-v2-agency-dropdown-header">
                    <div className="sidebar-v2-agency-icon">{agencyInitials}</div>
                    <div>
                        <div style={{fontWeight: 600, fontSize: 14}}>{agencyData?.name || ''}</div>
                        <div style={{fontSize: 12, color: 'var(--text-muted)'}}>{agencyData?.plan_name || ''}</div>
                    </div>
                </div>

                {/* Tools section */}
                <div className="sidebar-v2-agency-dropdown-section">
                    <div className="sidebar-v2-agency-dropdown-section-title">{t('sidebar.dropdown_tools')}</div>
                    <Link className="sidebar-v2-agency-dropdown-item" to="/agency/alerts-configuration" onClick={() => setShowAgencyDropdown(false)}>
                        <span className="icon"><Bell size={18}/></span>
                        {t('sidebar.dropdown_alerts')}
                    </Link>
                    {hasModule(AccessService.MODULE_DASHBO_MCP) && (
                        <Link className="sidebar-v2-agency-dropdown-item" to="/dashbo-connect" onClick={() => setShowAgencyDropdown(false)}>
                            <span className="icon"><LinkIcon size={18}/></span>
                            {t('sidebar.connect')}
                        </Link>
                    )}
                    {hasModule(AccessService.MODULE_SHEETS_SYNC) && (
                        <Link className="sidebar-v2-agency-dropdown-item" to="/sheets-sync" onClick={() => setShowAgencyDropdown(false)}>
                            <span className="icon"><DownloadSimple size={18}/></span>
                            {t('sidebar.exportacion')}
                        </Link>
                    )}
                    {hasModule(AccessService.MODULE_DATA_EXPLORER) && (
                        <a className="sidebar-v2-agency-dropdown-item" href="https://lookerstudio.google.com/" target="_blank" rel="noopener noreferrer" onClick={() => setShowAgencyDropdown(false)}>
                            <span className="icon"><ChartPie size={18}/></span>
                            {t('sidebar.looker_studio')}
                        </a>
                    )}
                </div>

                {/* Agency section */}
                <div className="sidebar-v2-agency-dropdown-section" style={{borderTop: '1px solid var(--border-color)'}}>
                    <div className="sidebar-v2-agency-dropdown-section-title">{t('sidebar.dropdown_agency')}</div>
                    <Link className="sidebar-v2-agency-dropdown-item" to="/agency/conections" onClick={() => setShowAgencyDropdown(false)}>
                        <span className="icon"><Plugs size={18}/></span>
                        {t('sidebar.dropdown_connections')}
                    </Link>
                    <Link className="sidebar-v2-agency-dropdown-item" to="/agency-team" onClick={() => setShowAgencyDropdown(false)}>
                        <span className="icon"><Users size={18}/></span>
                        {t('sidebar.dropdown_team')}
                    </Link>
                    <Link className="sidebar-v2-agency-dropdown-item" to="/agency/subscription" onClick={() => setShowAgencyDropdown(false)}>
                        <span className="icon"><CreditCard size={18}/></span>
                        {t('sidebar.dropdown_subscription')}
                    </Link>
                    <Link className="sidebar-v2-agency-dropdown-item" to="/agency/profile" onClick={() => setShowAgencyDropdown(false)}>
                        <span className="icon"><Buildings size={18}/></span>
                        {t('sidebar.dropdown_profile')}
                    </Link>
                </div>

            </div>
        </>
    );
}

// Keep backward-compatible export
export const LayoutSidebar = LayoutSidebarV2;
export default LayoutSidebar;
